import type { ParsedDocument } from '@vif/parsers';
import { detectLanguage } from '../lang/detect.js';
import { LIBRARY_MAX_SLOTS } from '../app/library-logic.js';
import {
  admitAndCommitImport,
  contentIdentity,
  findDocumentByIdentity,
  legacyFileHash,
  legacyTextHash,
  type DocumentFormat,
  type StoredDocument,
  type VifDB,
} from '../storage/index.js';

/**
 * Import pipeline: bytes/paste → @vif/parsers → PD-9 duplicate check →
 * library row → §6.6 legacy-resume claim. Parsing runs as plain async in
 * M1.3; M1.4 moves the parse into a Worker with progress + cancel (§6.7,
 * PD-2) without changing this surface.
 *
 * W-1 (Sol M-01, web half): the final commit goes through the storage-owned
 * admitAndCommitImport transaction — PD-9 re-check, the D-4 cap COUNT and all
 * writes in ONE IndexedDB readwrite transaction. The view's pre-checks are UX
 * fast-fails only; concurrent imports serialize at commit.
 */

export interface ImportProgress {
  (page: number, total: number): void;
}

export interface ParseOptions {
  readonly onProgress?: ImportProgress | undefined;
  readonly signal?: { readonly aborted: boolean } | undefined;
}

/** Parse transport seam (§6.7): the browser injects the Worker transport
 * (PD-2); tests and non-worker hosts use the default in-process parse. */
export type ParseFn = (
  format: DocumentFormat,
  bytes: Uint8Array,
  options?: ParseOptions,
) => Promise<ParsedDocument>;

export type ImportOutcome =
  | {
      readonly status: 'added';
      readonly doc: StoredDocument;
      /** True when an old-web-app position was re-keyed onto it (§6.6). */
      readonly legacyResumed: boolean;
    }
  /** The identical document was already in the library (PD-9). */
  | { readonly status: 'duplicate'; readonly doc: StoredDocument }
  /** D-4: no free slot — rejected by the admission transaction. */
  | { readonly status: 'library-full'; readonly limit: number };

const EXTENSION_FORMATS: Readonly<Record<string, DocumentFormat>> = {
  pdf: 'pdf',
  docx: 'docx',
  epub: 'epub',
  txt: 'txt',
  md: 'md',
};

export function formatForFileName(name: string): DocumentFormat | undefined {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  return EXTENSION_FORMATS[ext];
}

export const parseInProcess: ParseFn = async (format, bytes, options) => {
  const onProgress = options?.onProgress;
  const signal = options?.signal;
  switch (format) {
    case 'pdf': {
      // Lazy: pdf.js only ever loads when a PDF is actually imported (§12.2).
      // M1.3 runs pdf.js with its own worker (parsing already off the main
      // thread); M1.4 moves the whole parse into the app's Worker with the
      // in-thread pdf.js topology from the M1.1 memo.
      const [{ parsePdf }, pdfjs, worker] = await Promise.all([
        import('@vif/parsers/web'),
        import('pdfjs-dist'),
        import('pdfjs-dist/build/pdf.worker.mjs?url'),
      ]);
      pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
      return parsePdf(bytes, {
        ...(onProgress === undefined ? {} : { onProgress: (p, n) => onProgress(p, n) }),
        ...(signal === undefined ? {} : { signal }),
      });
    }
    case 'docx': {
      const { parseDocx } = await import('@vif/parsers');
      return parseDocx(bytes);
    }
    case 'epub': {
      const { parseEpub } = await import('@vif/parsers');
      return parseEpub(bytes);
    }
    case 'txt': {
      const { parsePlainTextBytes } = await import('@vif/parsers');
      return parsePlainTextBytes(bytes);
    }
    case 'md': {
      const { parseMarkdownBytes } = await import('@vif/parsers');
      return parseMarkdownBytes(bytes);
    }
    case 'paste':
      throw new Error('paste goes through importPaste()');
  }
};

async function store(
  db: VifDB,
  title: string,
  format: DocumentFormat,
  parsed: ParsedDocument,
  identity: Awaited<ReturnType<typeof contentIdentity>>,
  legacyHashes: readonly string[],
): Promise<ImportOutcome> {
  const lang = detectLanguage(parsed.blocks.map((b) => b.text).join(' '));
  // W-1: one transaction owns admission — identity re-check + cap COUNT +
  // document/blocks/legacy-resume writes commit atomically or not at all.
  return admitAndCommitImport(db, {
    title,
    format,
    lang,
    blocks: parsed.blocks,
    wordCount: parsed.wordCount,
    identity,
    maxDocuments: LIBRARY_MAX_SLOTS,
    legacyHashes,
  });
}

export async function importFile(
  db: VifDB,
  file: { name: string; bytes: Uint8Array },
  options?: ParseOptions & { parse?: ParseFn },
): Promise<ImportOutcome> {
  const format = formatForFileName(file.name);
  if (format === undefined) {
    throw new Error(`Unsupported format: .${file.name.split('.').pop() ?? '?'}`);
  }
  // Identity and legacy hash come BEFORE the parse: pdf.js takes ownership
  // of (detaches) the buffer, and a duplicate needs no parsing at all (PD-9).
  // This is a fast-fail; the admission transaction re-checks authoritatively.
  const identity = await contentIdentity(file.bytes);
  const existing = await findDocumentByIdentity(db, identity.contentHash, identity.size);
  if (existing !== undefined) return { status: 'duplicate', doc: existing };
  const legacyHash = await legacyFileHash(file.bytes);

  const parse = options?.parse ?? parseInProcess;
  const parsed = await parse(format, file.bytes, options);
  // PD-2: a cancelled import must leave NO partial library entry — nothing
  // has been written yet, and nothing will be if the abort landed late.
  if (options?.signal?.aborted === true) {
    throw Object.assign(new Error('cancelled'), { name: 'AbortError' });
  }
  const title = file.name.replace(/\.[^.]+$/, '');
  return store(db, title, format, parsed, identity, [legacyHash]);
}

export async function importPaste(db: VifDB, text: string, title: string): Promise<ImportOutcome> {
  const { parsePaste } = await import('@vif/parsers');
  const parsed = parsePaste(text);
  const bytes = new TextEncoder().encode(text);
  const identity = await contentIdentity(bytes);
  const existing = await findDocumentByIdentity(db, identity.contentHash, identity.size);
  if (existing !== undefined) return { status: 'duplicate', doc: existing };
  // W-1: paste routes through the same admission transaction — the D-4 cap is
  // enforced at commit, not by the paste modal's pre-check.
  return store(db, title, 'paste', parsed, identity, [legacyTextHash(text)]);
}
